import {
  type EmbedPlatform,
  IconMapPin,
  PlaceDetailTemplate,
} from '@fontbold/ui';
import { queryOptions } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { AppTabBar, AppTopBar } from '#/components/AppChrome';
import { AppNewsletter, openNewsletter } from '#/components/AppNewsletter';
import { useCookieConsent } from '#/lib/consent';
import { graphql } from '#/lib/gql';
import { fetchGraphQL } from '#/lib/graphql';
import { slugToLabel } from '#/lib/location';
import type { Space } from '#/lib/queries/space';
import { useRecordRecent } from '#/lib/recent';
import { relativeAgo } from '#/lib/relative-time';
import { useSaved } from '#/lib/saved';
import { embedHandle, embedPlatform, pageTitle, siteTitle } from '#/lib/site';
import { getSpace } from '#/lib/space-fn';
import { spaceLogo } from '#/lib/space-logo';
import { PLACE_DETAIL_TRANSLATIONS } from '#/lib/translations';

const GET_PLACE_BY_SLUG_QUERY = graphql(`
  query GetPlaceBySlug($spaceId: String!, $slug: String!) {
    place(spaceId: $spaceId) {
      getBySlug(slug: $slug, surface: DetailPage) {
        id
        name
        slug
        path
        citySlug
        districtSlug
        cover {
          cardUrl
          posterUrl
        }
        address {
          street
          number
          numberSuffix
          postalCode
          coordinates {
            latitude
            longitude
          }
        }
        labelDetails {
          name
          slug
        }
        embeds {
          all {
            id
            format
            url
            publishedAt
          }
          reels {
            id
            format
            url
            publishedAt
          }
        }
      }
    }
  }
`);

type InfoIconKey = 'address';

type PlaceDetailData = {
  id: string;
  name: string;
  path: string;
  area: string;
  cover: string | null;
  routeHref?: string;
  embed: {
    poster: string | null;
    platform?: EmbedPlatform;
    sourceUrl: string;
    ago?: string;
    views?: string;
  };
  info: Array<{
    icon: InfoIconKey;
    primary: string;
    secondary?: string;
  }>;
  tags: { name: string; slug: string }[];
  embeds: Array<{
    id: string;
    name: string;
    href: string;
    cover: string | null;
    platform?: EmbedPlatform;
    ago?: string;
  }>;
};

type LoaderData = {
  space: Space | null;
  place: PlaceDetailData | null;
};

function streetLine(address: {
  street: string;
  number?: string | null;
  numberSuffix?: string | null;
  postalCode?: string | null;
}): string {
  const number = address.number
    ? `${address.number}${address.numberSuffix ?? ''}`
    : '';
  const postal = address.postalCode ? `, ${address.postalCode}` : '';
  return `${[address.street, number].filter(Boolean).join(' ')}${postal}`;
}

const placeQueries = {
  detail: (spaceId: string, slug: string) =>
    queryOptions({
      queryKey: ['places', 'detail', spaceId, slug] as const,
      queryFn: async (): Promise<PlaceDetailData | null> => {
        const data = await fetchGraphQL(GET_PLACE_BY_SLUG_QUERY, {
          spaceId,
          slug,
        });
        const node = data?.place?.getBySlug;
        if (!node) return null;

        const embeds = (node.embeds?.all ?? []).filter(
          (entry): entry is NonNullable<typeof entry> => Boolean(entry?.url),
        );
        const featuredEmbed =
          (node.embeds?.reels ?? []).find((entry) => Boolean(entry?.url)) ??
          embeds[0] ??
          null;
        const cover = node.cover?.cardUrl ?? null;
        const poster = node.cover?.posterUrl ?? null;
        const area = [
          slugToLabel(node.districtSlug),
          slugToLabel(node.citySlug),
        ]
          .filter(Boolean)
          .join(', ');

        const seen = new Set<string>();
        const tags = (node.labelDetails ?? [])
          .flatMap((label) =>
            label?.name && label.slug
              ? [{ name: label.name, slug: label.slug }]
              : [],
          )
          .filter((label) => {
            if (seen.has(label.slug)) return false;
            seen.add(label.slug);
            return true;
          })
          .slice(0, 8);

        const info: PlaceDetailData['info'] = [];
        const routeHref = node.address
          ? `https://www.google.com/maps/dir/?api=1&destination=${node.address.coordinates.latitude},${node.address.coordinates.longitude}`
          : undefined;
        if (node.address) {
          info.push({
            icon: 'address',
            primary: streetLine(node.address),
            secondary: 'Route uitzetten',
          });
        }

        return {
          id: node.id,
          name: node.name,
          path: node.path,
          area,
          cover,
          routeHref,
          embed: {
            poster,
            platform: embedPlatform(featuredEmbed?.format),
            sourceUrl: featuredEmbed?.url ?? '#',
            ago: relativeAgo(featuredEmbed?.publishedAt),
          },
          info,
          tags,
          embeds: embeds.map((embed) => ({
            id: embed.id,
            name: node.name,
            href: embed.url,
            cover: poster,
            platform: embedPlatform(embed.format),
            ago: relativeAgo(embed.publishedAt),
          })),
        };
      },
    }),
};

const INFO_ICON: Record<InfoIconKey, ReactNode> = {
  address: <IconMapPin size={18} />,
};

export const Route = createFileRoute('/$city/$district/$place')({
  loader: async ({ params, context }): Promise<LoaderData> => {
    const space = await getSpace();
    if (!space) return { space: null, place: null };

    const place = await context.queryClient.ensureQueryData(
      placeQueries.detail(space.id, params.place),
    );
    return { space, place };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.place
          ? pageTitle(loaderData.place.name, loaderData.space)
          : siteTitle(loaderData?.space),
      },
    ],
  }),
  component: PlacePage,
});

function PlacePage() {
  const { space, place } = Route.useLoaderData();
  const router = useRouter();
  const { has, toggle } = useSaved();
  const { functional } = useCookieConsent();

  useRecordRecent(
    place
      ? { id: place.id, name: place.name, path: place.path, cover: place.cover }
      : null,
  );

  if (!space || !place) return null;

  const logo = spaceLogo(space);

  return (
    <>
      <PlaceDetailTemplate
        topBar={
          <AppTopBar
            space={space}
            active="kaart"
            onNewsletter={openNewsletter}
          />
        }
        tabBar={<AppTabBar space={space} active="kaart" />}
        translations={PLACE_DETAIL_TRANSLATIONS}
        name={place.name}
        area={place.area}
        backLabel="Terug"
        onBack={() => router.history.back()}
        embed={{
          ...place.embed,
          handle: place.embed.platform
            ? embedHandle(place.embed.sourceUrl, place.embed.platform, space)
            : '',
        }}
        badges={{}}
        info={place.info.map((row, index) => ({
          id: `${row.icon}-${index}`,
          icon: INFO_ICON[row.icon],
          primary: row.primary,
          secondary: row.secondary,
        }))}
        tags={place.tags.map((tag) => ({
          label: tag.name,
          href: `/kaart?tag=${encodeURIComponent(tag.slug)}`,
        }))}
        embeds={place.embeds}
        routeHref={place.routeHref}
        saved={has(place.id)}
        onToggleSave={() => toggle(place.id)}
        showSave={functional}
        onShare={() => {
          if (typeof navigator !== 'undefined' && navigator.share) {
            navigator
              .share({ title: place.name, url: window.location.href })
              .catch(() => {});
          }
        }}
        onReport={
          space.contactEmail
            ? () => {
                window.location.href = `mailto:${space.contactEmail}?subject=Info klopt niet`;
              }
            : undefined
        }
      />
      <AppNewsletter spaceId={space.id} logo={logo} />
    </>
  );
}
